import { st } from "~/sassmall/i18n";
import type { FetchOptions } from "ofetch";
import {
  SASSMALL_CUSTOMER_API_PREFIX,
  SASSMALL_TENANT_DOMAIN_HEADER,
} from "../constants";
import {
  clearSassmallAuth,
  getSassmallTenantDomain,
  getSassmallToken,
  setSassmallTenantDomain,
  setSassmallToken,
} from "../storage";

export interface SassmallApiResponse<T = any> {
  code?: number;
  message?: string;
  msg?: string;
  success?: boolean;
  data?: T;
  [key: string]: any;
}

export interface SassmallRequestOptions extends FetchOptions {
  skipAuth?: boolean;
  skipTenant?: boolean;
  tenantDomain?: string;
}

const MAX_SAFE_INTEGER_TEXT = String(Number.MAX_SAFE_INTEGER);

function isUnsafeIntegerToken(token: string) {
  const unsigned = token.startsWith("-") ? token.slice(1) : token;
  if (!/^\d+$/.test(unsigned)) return false;
  return unsigned.length > MAX_SAFE_INTEGER_TEXT.length
    || (unsigned.length === MAX_SAFE_INTEGER_TEXT.length && unsigned > MAX_SAFE_INTEGER_TEXT);
}

export function preserveSassmallLargeIntegers(rawJson: string) {
  let result = "";
  let token = "";
  let inString = false;
  let escaped = false;

  for (const char of rawJson) {
    if (inString) {
      result += char;
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') {
      if (token) {
        result += isUnsafeIntegerToken(token) ? `"${token}"` : token;
        token = "";
      }
      inString = true;
      result += char;
      continue;
    }
    if (/[0-9eE+\-.]/.test(char)) {
      token += char;
      continue;
    }
    if (token) {
      result += isUnsafeIntegerToken(token) ? `"${token}"` : token;
      token = "";
    }
    result += char;
  }
  if (token) result += isUnsafeIntegerToken(token) ? `"${token}"` : token;
  return result;
}

function parseSassmallResponse(rawValue: string) {
  if (!rawValue?.trim()) return rawValue;
  try {
    return JSON.parse(preserveSassmallLargeIntegers(rawValue.replace(/^\uFEFF/, "")));
  }
  catch {
    return rawValue;
  }
}

function normalizeBearerToken(token: string) {
  if (!token) return "";
  return /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
}

function normalizePath(path: string) {
  const normalized = String(path || "").trim();
  if (normalized.startsWith(SASSMALL_CUSTOMER_API_PREFIX)) return normalized;
  return `${SASSMALL_CUSTOMER_API_PREFIX}${normalized.startsWith("/") ? "" : "/"}${normalized}`;
}

function normalizeUpstreamPath(path: string) {
  const normalized = String(path || "").trim();
  const withoutPrefix = normalized.startsWith(SASSMALL_CUSTOMER_API_PREFIX)
    ? normalized.slice(SASSMALL_CUSTOMER_API_PREFIX.length)
    : normalized;
  return withoutPrefix.startsWith("/") ? withoutPrefix : `/${withoutPrefix}`;
}

export function isSassmallResponseSuccess(response?: SassmallApiResponse | null) {
  return Boolean(
    response
    && (Number(response.code) === 200
      || response.success === true
      || String(response.message || "").toLowerCase() === "success"),
  );
}

export function getSassmallResponseMessage(response?: SassmallApiResponse | null, fallback = "") {
  return String(response?.message || response?.msg || fallback);
}

export async function sassmallRequest<T = any>(
  path: string,
  options: SassmallRequestOptions = {},
): Promise<SassmallApiResponse<T>> {
  const {
    skipAuth,
    skipTenant,
    tenantDomain: explicitTenantDomain,
    headers: optionHeaders,
    ...fetchOptions
  } = options;
  const headers = new Headers(optionHeaders as HeadersInit | undefined);
  if (!headers.has("Accept-Language")) {
    const locale = useNuxtApp().$i18n?.locale;
    const language = typeof locale === "string" ? locale : locale?.value;
    if (language) headers.set("Accept-Language", String(language).replace(/_/g, "-"));
  }
  const token = getSassmallToken();
  // SSR 没有 localStorage，从当前页面请求中继承租户域名，否则服务端首屏请求会
  // 缺少租户上下文，分类和文章数据只能等到浏览器 hydration 后才加载出来。
  const serverRequestHeaders = process.server
    ? useRequestHeaders(["x-tenant-domain", "host"])
    : {};
  const requestHost = String(serverRequestHeaders["x-tenant-domain"] || serverRequestHeaders.host || "")
    .split(",")[0]
    .trim()
    .replace(/^https?:\/\//i, "")
    .split(":")[0];
  const tenantDomain = String(explicitTenantDomain || getSassmallTenantDomain() || requestHost
    || (process.client ? window.location.hostname : "")).trim();

  if (!skipAuth && token) headers.set("authorization", normalizeBearerToken(token));
  if (!skipTenant && tenantDomain) headers.set(SASSMALL_TENANT_DOMAIN_HEADER, tenantDomain);
  if (!headers.has("Content-Type") && fetchOptions.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  try {
    const runtimeConfig = useRuntimeConfig();
    const requestUrl = process.server
      ? `${String(runtimeConfig.sassmallCustomerApiTarget || "").replace(/\/$/, "")}${normalizeUpstreamPath(path)}`
      : normalizePath(path);
    const response = await $fetch.raw<SassmallApiResponse<T>>(requestUrl, {
      ...fetchOptions,
      headers,
      parseResponse: parseSassmallResponse,
    } as FetchOptions);
    const responseToken = response.headers.get("authorization");
    const responseTenant = response.headers.get(SASSMALL_TENANT_DOMAIN_HEADER);
    if (responseToken) setSassmallToken(responseToken.replace(/^Bearer\s+/i, ""));
    if (responseTenant) setSassmallTenantDomain(responseTenant);
    const responseData = response._data as SassmallApiResponse<T>;
    if (responseData && typeof responseData === "object" && responseTenant) {
      Object.defineProperty(responseData, "__tenantDomain", {
        configurable: true,
        enumerable: false,
        value: responseTenant,
      });
    }
    return responseData;
  }
  catch (error: any) {
    if (Number(error?.response?.status) === 401 &&
      !String(path).includes("/destination-country-configs/recommendation")) clearSassmallAuth();
    const responseData = error?.response?._data || error?.data;
    const message = getSassmallResponseMessage(responseData, error?.message || st("请求失败"));
    const nextError = new Error(message);
    (nextError as any).status = error?.response?.status;
    (nextError as any).response = responseData;
    throw nextError;
  }
}
